import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef, OnChanges, SimpleChanges, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Observable, takeUntil, catchError, of } from 'rxjs';
import { DestroyService, AlertService } from '@selfai-platform/shared';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Button } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { MessageModule } from 'primeng/message';
import { TooltipModule } from 'primeng/tooltip';
import { ThreadManagementService, AgentService } from '../../services';
import { ChatThread, Agent } from '../../models';
import { VisibilityChangeEvent, ThreadSelectedEvent, ErrorEvent } from '../../types';
import { DialogService, provideDialogService } from '../../../dialog';
import { ThreadNameInputDialogComponent, ThreadNameInputResult } from '../thread-name-input-dialog';
import { ThreadDeleteConfirmationDialogComponent, ThreadDeleteConfirmationResult } from '../thread-delete-confirmation-dialog';
import { AgentSelectionDialogComponent, AgentSelectionResult } from '../agent-selection-dialog';

@Component({
  selector: 'selfai-platform-chat-threads-panel',
  standalone: true,
  imports: [CommonModule, TranslateModule, Button, TagModule, MessageModule, TooltipModule],
  providers: [...provideDialogService(), DestroyService],
  templateUrl: './chat-threads-panel.component.html',
  styleUrls: ['./chat-threads-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatThreadsPanelComponent implements OnInit, OnChanges {
  @Input() isVisible = true;
  @Input() selectedAgent: Agent | null = null;
  @Output() visibilityChange = new EventEmitter<VisibilityChangeEvent>();
  @Output() threadSelected = new EventEmitter<ThreadSelectedEvent>();
  @Output() errorOccurred = new EventEmitter<ErrorEvent>();
  @Output() agentSelected = new EventEmitter<Agent>();

  public threads$: Observable<ChatThread[]>;
  public activeThread$: Observable<ChatThread | null>;
  public currentThread: ChatThread | null = null;

  public isAgentHealthy = false;
  public isLoadingAgents = true;
  public availableAgents: Agent[] = [];

  constructor(
    private readonly threadManagementService: ThreadManagementService,
    private readonly agentService: AgentService,
    private readonly cdr: ChangeDetectorRef,
    private readonly dialogService: DialogService,
    private readonly destroy$: DestroyService,
    private readonly translateService: TranslateService,
    private readonly alertService: AlertService
  ) {
    this.threads$ = this.threadManagementService.threads$;
    this.activeThread$ = this.threadManagementService.activeThread$;

    this.activeThread$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((thread) => {
      this.currentThread = thread;
      this.cdr.markForCheck();
    });
  }

  public ngOnInit(): void {
    this.initializeAgents();
  }

  public ngOnChanges(changes: SimpleChanges): void {
  }

  public createNewThread(agent: Agent): void {
    if (!agent) {
      this.errorOccurred.emit({
        error: this.translateService.instant('chat.errors.please-select-agent'),
        context: 'createNewThread'
      });
      return;
    }

    this.threadManagementService.createEmptyThread(agent.type);
  }

  public selectThread(thread: ChatThread): void {
    this.threadManagementService.setActiveThread(thread);
    this.threadSelected.emit({ thread });
  }

  public deleteThread(thread: ChatThread, event: Event): void {
    event.stopPropagation();

    this.dialogService.showDialog(ThreadDeleteConfirmationDialogComponent, {
      header: 'Confirm Deletion',
      width: '450px',
      modal: true,
      closable: true,
      closeOnEscape: true,
      data: {
        threadTitle: thread.title,
        threadId: thread.id
      }
    } as any).pipe(
      takeUntil(this.destroy$)
    ).subscribe((result: ThreadDeleteConfirmationResult) => {
      if (result?.confirmed) {
        this.threadManagementService.deleteThread(thread.id)
          .pipe(
            catchError((error) => {
              this.alertService.error(this.translateService.instant('chat.errors.error-deleting-thread') + ':', error);
              this.errorOccurred.emit({
                error: this.translateService.instant('chat.errors.failed-delete-thread'),
                context: 'deleteThread'
              });
              return of(null);
            }),
            takeUntil(this.destroy$)
          )
          .subscribe();
      }
    });
  }

  public renameThread(thread: ChatThread, newTitle: string): void {
    if (newTitle.trim() && newTitle !== thread.title) {
      this.threadManagementService.updateThreadTitle(thread.id, newTitle.trim())
        .pipe(
          catchError((error) => {
            this.alertService.error(this.translateService.instant('chat.errors.error-renaming-thread') + ':', error);
            this.errorOccurred.emit({
              error: this.translateService.instant('chat.errors.failed-rename-thread'),
              context: 'renameThread'
            });
            return of(null);
          }),
          takeUntil(this.destroy$)
        )
        .subscribe();
    }
  }

  public toggleVisibility(): void {
    this.isVisible = !this.isVisible;
    this.visibilityChange.emit({ isVisible: this.isVisible });
  }


  public getThreadDisplayTitle(thread: ChatThread): string {
    return thread.title || thread.agentThreadId || thread.id;
  }


  private initializeAgents(): void {
    this.agentService.agentHealth$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((isHealthy) => {
      this.isAgentHealthy = isHealthy;
    });

    this.agentService.availableAgents$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((agents) => {
      this.availableAgents = agents;
      this.isLoadingAgents = false;
    });

    this.agentService.checkAgentHealth().pipe(
      takeUntil(this.destroy$)
    ).subscribe();
  }

  public showNewThreadDialog(): void {
    this.dialogService.showDialog(AgentSelectionDialogComponent, {
      header: this.translateService.instant('chat.threads.new-conversation'),
      width: '500px',
      modal: true,
      closable: true,
      closeOnEscape: true,
    } as any).pipe(
      takeUntil(this.destroy$)
    ).subscribe((result: AgentSelectionResult | null) => {
      if (result?.selectedAgent) {
        this.createNewThread(result.selectedAgent);
      }
    });
  }

  public refreshAgents(): void {
    this.isLoadingAgents = true;
    this.cdr.markForCheck();

    this.agentService.refreshAgents().pipe(
      catchError((error) => {
        this.alertService.error(this.translateService.instant('chat.errors.failed-to-refresh-agents') + ':', error);
        this.errorOccurred.emit({
          error: this.translateService.instant('chat.errors.failed-refresh-agents'),
          context: 'refreshAgents'
        });
        return of(null);
      }),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.isLoadingAgents = false;
      this.cdr.markForCheck();
    });
  }

}
